import { useMemo, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Calendar, Gauge, Users, MapPin, SlidersHorizontal, X } from "lucide-react";
import {
  fyndanuListingsQuery,
  formatPrice,
  formatMil,
  licenseClass,
} from "@/lib/vehicles";
import type { FyndanuListing, VehicleCategory } from "@/lib/fyndanu.functions";
import { FavoriteButton } from "@/components/FavoriteButton";
import { DeliveryChecks } from "@/components/DeliveryChecks";
import { vehicleSlug } from "@/lib/vehicle-slug";
import { imgSrcSet, CARD_SIZES } from "@/lib/img";

type Props = {
  category: VehicleCategory | "all";
  emptyLabel?: string;
  lockedBrand?: string;
};

type Sort = "featured" | "price-asc" | "price-desc" | "year-desc" | "newest";

const SORTS: { value: Sort; label: string }[] = [
  { value: "featured", label: "Rekommenderade" },
  { value: "newest", label: "Nyast inlagt" },
  { value: "price-asc", label: "Pris: lägst först" },
  { value: "price-desc", label: "Pris: högst först" },
  { value: "year-desc", label: "Årsmodell: nyast" },
];

export function VehicleBrowser({ category, emptyLabel = "Inga fordon matchar just nu.", lockedBrand }: Props) {
  const { data } = useSuspenseQuery(fyndanuListingsQuery);
  const scoped = useMemo(() => {
    const base = category === "all" ? data.items : data.items.filter((i) => i.category === category);
    if (!lockedBrand) return base;
    const needle = lockedBrand.toLowerCase();
    return base.filter((i) => (i.brand ?? "").toLowerCase() === needle || (i.title ?? "").toLowerCase().includes(needle));
  }, [data.items, category, lockedBrand]);

  const brands = useMemo(() => {
    const set = new Set<string>();
    for (const i of scoped) if (i.brand) set.add(i.brand);
    return Array.from(set).sort();
  }, [scoped]);

  const [brand, setBrand] = useState<string>("");
  const [priceMax, setPriceMax] = useState<number>(0);
  const [yearMin, setYearMin] = useState<number>(0);
  const [license, setLicense] = useState<"" | "B" | "any">("");
  const [minSpots, setMinSpots] = useState<number>(0);
  const [sort, setSort] = useState<Sort>("featured");
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    let list = scoped.filter((i) => {
      if (brand && i.brand !== brand) return false;
      if (priceMax > 0 && (i.price ?? 0) > priceMax) return false;
      if (yearMin > 0 && (i.year ?? 0) < yearMin) return false;
      if (license === "B" && licenseClass(i.totalWeight) !== "B") return false;
      if (minSpots > 0 && (i.sleepingSpots ?? 0) < minSpots) return false;
      return true;
    });

    switch (sort) {
      case "price-asc":
        list = list.slice().sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity));
        break;
      case "price-desc":
        list = list.slice().sort((a, b) => (b.price ?? -1) - (a.price ?? -1));
        break;
      case "year-desc":
        list = list.slice().sort((a, b) => (b.year ?? 0) - (a.year ?? 0));
        break;
      case "newest":
        list = list.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        break;
      default:
        break;
    }
    return list;
  }, [scoped, brand, priceMax, yearMin, license, minSpots, sort]);

  const reset = () => {
    setBrand("");
    setPriceMax(0);
    setYearMin(0);
    setLicense("");
    setMinSpots(0);
  };

  const active =
    (brand ? 1 : 0) + (priceMax ? 1 : 0) + (yearMin ? 1 : 0) + (license ? 1 : 0) + (minSpots ? 1 : 0);

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:bg-secondary"
        >
          <SlidersHorizontal size={14} /> Filter {active > 0 && <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] text-primary-foreground">{active}</span>}
        </button>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as Sort)}
          className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-muted-foreground">
          {filtered.length} av {scoped.length} fordon
        </span>
      </div>

      {showFilters && (
        <div className="mb-8 rounded-3xl border border-border bg-card p-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <label className="text-sm">
              <span className="mb-1 block font-medium">Märke</span>
              <select value={brand} onChange={(e) => setBrand(e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2">
                <option value="">Alla</option>
                {brands.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Max pris (kr)</span>
              <input type="number" min={0} step={10000} value={priceMax || ""} onChange={(e) => setPriceMax(Number(e.target.value) || 0)} placeholder="Ingen gräns" className="w-full rounded-lg border border-border bg-background px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Från årsmodell</span>
              <input type="number" min={1990} max={new Date().getFullYear() + 1} value={yearMin || ""} onChange={(e) => setYearMin(Number(e.target.value) || 0)} placeholder="Alla" className="w-full rounded-lg border border-border bg-background px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Körkort</span>
              <select value={license} onChange={(e) => setLicense(e.target.value as "" | "B")} className="w-full rounded-lg border border-border bg-background px-3 py-2">
                <option value="">Alla</option>
                <option value="B">Endast B-kort (≤ 3500 kg)</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium">Min bäddar</span>
              <select value={minSpots} onChange={(e) => setMinSpots(Number(e.target.value))} className="w-full rounded-lg border border-border bg-background px-3 py-2">
                <option value={0}>Alla</option>
                <option value={2}>2+</option>
                <option value={4}>4+</option>
                <option value={5}>5+</option>
                <option value={6}>6+</option>
              </select>
            </label>
          </div>
          {active > 0 && (
            <button type="button" onClick={reset} className="mt-4 inline-flex items-center gap-1 text-sm text-accent hover:underline">
              <X size={14} /> Rensa filter
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-3xl border border-border bg-card p-10 text-center">
          <p className="text-muted-foreground">{emptyLabel}</p>
          {active > 0 && (
            <button type="button" onClick={reset} className="mt-3 text-sm text-accent underline">
              Rensa filter
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((item) => <VehicleCard key={item.id} item={item} />)}
        </div>
      )}
    </div>
  );
}

function VehicleCard({ item }: { item: FyndanuListing }) {
  const mil = formatMil(item.mileage);
  const lic = licenseClass(item.totalWeight);
  return (
    <Link
      to="/fordon/$id"
      params={{ id: vehicleSlug(item) }}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-xl"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
        <img
          src={item.image}
          srcSet={imgSrcSet(item.image)}
          sizes={CARD_SIZES}
          alt={item.title}
          width={800}
          height={600}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
          style={{ filter: "contrast(1.05) saturate(1.08)" }}
        />
        <div className="absolute right-3 top-3 rounded-full bg-background/95 px-3 py-1 text-xs font-semibold shadow-sm backdrop-blur">
          {formatPrice(item.price)}
        </div>
        <FavoriteButton id={item.id} className="absolute right-3 bottom-3" />
        {(item.category === "husbil" || item.category === "husvagn") && (
          <div className="absolute left-3 bottom-3 rounded-full bg-background/95 px-2 py-1 shadow-sm backdrop-blur">
            <DeliveryChecks compact />
          </div>
        )}
        <div className="absolute left-3 top-3 flex gap-2">
          {item.category !== "annat" && (
            <span className="rounded-full bg-primary/95 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground shadow-sm backdrop-blur">
              {item.category}
            </span>
          )}
          {lic === "B" && (
            <span className="rounded-full bg-emerald-600/95 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white shadow-sm backdrop-blur">
              B-kort
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-display text-xl leading-tight line-clamp-2 group-hover:text-accent">
          {item.title}
        </h3>
        <dl className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {item.year && <div className="inline-flex items-center gap-1"><Calendar size={12} /> {item.year}</div>}
          {mil && <div className="inline-flex items-center gap-1"><Gauge size={12} /> {mil}</div>}
          {item.sleepingSpots != null && (
            <div className="inline-flex items-center gap-1"><Users size={12} /> {item.sleepingSpots} bäddar</div>
          )}
        </dl>
        <div className="mt-auto pt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            {item.location && (<><MapPin size={14} /> {item.location}</>)}
          </span>
          <span className="inline-flex items-center gap-1 text-accent font-medium">
            Visa <ArrowUpRight size={14} />
          </span>
        </div>
      </div>
    </Link>
  );
}
